import type {
  Board,
  Dependency,
  LegacyBoardDetail,
  LegacyDocument,
  LegacyNode,
  Ticket,
  TicketRead,
} from "./contracts";
import { FieldConflict } from "./conflicts";
import {
  cacheFor,
  forgetBoard,
  loadBoard,
  prefixFromTicket,
  replaceCachedTicket,
} from "./state";
import { ApiProblem, newIdempotencyKey, request } from "./transport";

interface FlatNode {
  node: LegacyNode;
  parentId: string | null;
  columnKey: string | null;
  scopeIndex: number;
}

const pendingCreates = new Map<string, { key: string; body: Record<string, unknown> }>();
const PATCH_CONFLICT_CODES = new Set(["ticket_deleted"]);
const MOVE_CONFLICT_CODES = new Set([
  "ticket_deleted",
  "parent_deleted",
  "hierarchy_cycle",
  "invalid_self_anchor",
  "anchor_scope_mismatch",
  "anchor_not_found",
]);

export function isResourceConflict(error: unknown, conflictCodes: ReadonlySet<string>): boolean {
  if (!(error instanceof ApiProblem)) return false;
  if (error.status === 412) return true;
  return error.status === 409 && conflictCodes.has(String(error.problem.code || ""));
}

function flatten(cards: LegacyNode[]): Map<string, FlatNode> {
  const result = new Map<string, FlatNode>();
  const visit = (nodes: LegacyNode[], parentId: string | null): void => {
    // Top-level nodes from every column share one document array. Position is
    // meaningful only among siblings in the same parent/column scope.
    const columnIndexes = new Map<string, number>();
    nodes.forEach((node, index) => {
      const columnKey = parentId ? null : String(node.col || "backlog");
      let scopeIndex = index;
      if (columnKey !== null) {
        scopeIndex = columnIndexes.get(columnKey) ?? 0;
        columnIndexes.set(columnKey, scopeIndex + 1);
      }
      result.set(node.id, {
        node,
        parentId,
        columnKey,
        scopeIndex,
      });
      visit(node.children || [], node.id);
    });
  };
  visit(cards, null);
  return result;
}

function patchFor(node: LegacyNode): Record<string, unknown> {
  return {
    title: String(node.title || ""),
    ...(typeof node.note === "string" ? { note: node.note } : {}),
    type: String(node.type || "task"),
    priority: String(node.priority || "med"),
    area: String(node.area || ""),
    expanded: node.expanded !== false,
    timeline_bucket: node.bucket || null,
    ...(node.status ? { status: node.status } : {}),
  };
}

function ticketComparable(ticket: TicketRead): Record<string, unknown> {
  return {
    title: ticket.title,
    note: ticket.note,
    type: ticket.type,
    priority: ticket.priority,
    area: ticket.area,
    expanded: ticket.expanded,
    timeline_bucket: ticket.timeline_bucket,
    status: ticket.status,
  };
}

function changedFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): string[] {
  return Object.keys(after).filter(
    (field) => JSON.stringify(before[field]) !== JSON.stringify(after[field]),
  );
}

async function ticketConflictState(boardId: string, ticketId: string): Promise<Ticket> {
  const [ticket, dependencies] = await Promise.all([
    request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${ticketId}`),
    request<Dependency[]>(
      "GET",
      `/api/v1/boards/${boardId}/tickets/${ticketId}/dependencies`,
    ),
  ]);
  return Object.assign(ticket.body, {
    blockedBy: dependencies.body.map((item) => item.blocking_ticket_id),
  });
}

async function patchWithConflict(
  boardId: string,
  ticket: TicketRead,
  baseline: Record<string, unknown>,
  patch: Record<string, unknown>,
): Promise<TicketRead> {
  const fields = changedFields(baseline, patch);
  if (!fields.length) return ticket;
  const selected = Object.fromEntries(fields.map((field) => [field, patch[field]]));
  try {
    const updated = await request<Ticket>(
      "PATCH",
      `/api/v1/boards/${boardId}/tickets/${ticket.id}`,
      selected,
      { etag: ticket.version },
    );
    replaceCachedTicket(boardId, updated.body);
    return updated.body;
  } catch (error) {
    if (!isResourceConflict(error, PATCH_CONFLICT_CODES)) throw error;
    const latest = (
      await request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${ticket.id}`)
    ).body;
    if (latest.deleted_at) {
      throw new FieldConflict(ticket.id, fields, selected, latest);
    }
    // Only fields explicitly changed from the caller's baseline are present in
    // selected. Reapply those local deltas to the latest version while leaving
    // every untouched remote field intact.
    const retried = await request<Ticket>(
      "PATCH",
      `/api/v1/boards/${boardId}/tickets/${ticket.id}`,
      selected,
      { etag: latest.version },
    );
    replaceCachedTicket(boardId, retried.body);
    return retried.body;
  }
}

function remapDocument(document: LegacyDocument, mapping: Record<string, string>): void {
  const visit = (nodes: LegacyNode[]): void => {
    for (const node of nodes) {
      if (mapping[node.id]) node.id = mapping[node.id] as string;
      node.blockedBy = (node.blockedBy || []).map((id) => mapping[id] || id);
      visit(node.children || []);
    }
  };
  visit(document.cards);
}

async function saveRelational(
  boardId: string,
  document: LegacyDocument,
  baselineDocument: LegacyDocument,
): Promise<LegacyBoardDetail> {
  let cache = cacheFor(boardId);
  if (!cache) {
    await loadBoard(boardId);
    cache = cacheFor(boardId);
  }
  if (!cache) throw new Error("Relational board cache is unavailable");
  const idMappings: Record<string, string> = {};
  let current = flatten(document.cards);
  const baseline = flatten(baselineDocument.cards);

  if (
    document.name !== cache.board.name ||
    document.view !== cache.board.settings.view ||
    document.tlUnit !== cache.board.settings.timeline_unit
  ) {
    const boardUpdate = await request<Board>(
      "PATCH",
      `/api/v1/boards/${boardId}`,
      {
        name: document.name,
        settings: { view: document.view, timeline_unit: document.tlUnit },
      },
      { etag: cache.boardEtag },
    );
    cache.board = boardUpdate.body;
    cache.boardEtag = boardUpdate.etag || String(boardUpdate.body.version);
  }

  // Preorder flattening guarantees a newly-created parent exists before its children.
  for (const [localId, flat] of current) {
    if (cache.tickets.has(localId)) continue;
    // Undo may recreate a deleted ticket from a compact historical snapshot.
    // Never submit its preview/default empty note as the restored content.
    if (typeof flat.node.note !== "string" && typeof flat.node.notePreview === "string") {
      const full = (await request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${localId}`)).body;
      flat.node.note = full.note;
      delete flat.node.notePreview;
    }
    const parentId = flat.parentId ? idMappings[flat.parentId] || flat.parentId : null;
    const createBody: Record<string, unknown> = {
      ...patchFor(flat.node),
      parent_id: parentId,
      column_id: parentId ? null : cache.columnsByKey.get(flat.columnKey || "")?.id,
      metadata: {},
    };
    let pending = pendingCreates.get(`${boardId}:${localId}`);
    if (!pending) {
      pending = { key: newIdempotencyKey(), body: createBody };
      pendingCreates.set(`${boardId}:${localId}`, pending);
    }
    const created = await request<Ticket>(
      "POST",
      `/api/v1/boards/${boardId}/tickets`,
      pending.body,
      { idempotencyKey: pending.key },
    );
    pendingCreates.delete(`${boardId}:${localId}`);
    idMappings[localId] = created.body.id;
    flat.node.id = created.body.id;
    flat.node.ticketNumber = created.body.number;
    flat.node.ticketPrefix = prefixFromTicket(created.body);
    cache.tickets.set(created.body.id, created.body);
  }
  remapDocument(document, idMappings);
  current = flatten(document.cards);

  for (const [ticketId, flat] of current) {
    const ticket = cache.tickets.get(ticketId);
    if (!ticket) continue;
    const baselineNode = baseline.get(ticketId)?.node;
    const baselineFields = baselineNode
      ? { ...ticketComparable(ticket), ...patchFor(baselineNode) }
      : ticketComparable(ticket);
    const patch = patchFor(flat.node);
    // Avoid an async microtask hop for every unchanged ticket. On a large board
    // an autosave should await only the one draft that actually changed.
    if (!changedFields(baselineFields, patch).length) continue;
    const updated = await patchWithConflict(
      boardId,
      ticket,
      baselineFields,
      patch,
    );
    cache.tickets.set(ticketId, updated);
  }

  // Moves are commands, not field patches. Fetch the current ETag before each
  // move because reordering may legitimately version neighboring tickets.
  for (const [ticketId, flat] of current) {
    const old = baseline.get(ticketId);
    if (!old) continue;
    const moved =
      old.parentId !== flat.parentId ||
      old.columnKey !== flat.columnKey ||
      old.scopeIndex !== flat.scopeIndex;
    if (!moved) continue;
    const scope = [...current.values()].filter(
      (item) => item.parentId === flat.parentId && item.columnKey === flat.columnKey,
    );
    const index = scope.findIndex((item) => item.node.id === ticketId);
    const previous = index > 0 ? scope[index - 1]?.node.id : undefined;
    const next = index >= 0 && index < scope.length - 1 ? scope[index + 1]?.node.id : undefined;
    if (!cache.tickets.has(ticketId)) continue;
    // Moving one ticket can renumber and version-bump every ticket in its scope.
    // Refresh here so a subsequent move in the same save does not treat that
    // expected sibling update as a concurrent-edit conflict.
    const currentTicket = (
      await request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${ticketId}`)
    ).body;
    cache.tickets.set(ticketId, currentTicket);
    const body = {
      parent_id: flat.parentId,
      column_id: flat.parentId ? null : cache.columnsByKey.get(flat.columnKey || "")?.id,
      ...(previous
        ? { after_ticket_id: previous }
        : next
          ? { before_ticket_id: next }
          : {}),
    };
    try {
      const movedTicket = await request<Ticket>(
        "POST",
        `/api/v1/boards/${boardId}/tickets/${ticketId}/move`,
        body,
        { etag: currentTicket.version, idempotencyKey: newIdempotencyKey() },
      );
      cache.tickets.set(ticketId, movedTicket.body);
    } catch (error) {
      if (!isResourceConflict(error, MOVE_CONFLICT_CODES)) throw error;
      const remote = (
        await request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${ticketId}`)
      ).body;
      throw new FieldConflict(
        ticketId,
        ["parent_id", "column_id", "sort_order"],
        body,
        remote,
      );
    }
  }

  const deleted = [...baseline.keys()].filter((ticketId) => !current.has(ticketId)).reverse();
  for (const ticketId of deleted) {
    const currentTicket = cache.tickets.get(ticketId);
    if (!currentTicket) continue;
    try {
      await request<void>("DELETE", `/api/v1/boards/${boardId}/tickets/${ticketId}`, undefined, {
        etag: currentTicket.version,
      });
      cache.tickets.delete(ticketId);
      cache.dependencies.delete(ticketId);
    } catch (error) {
      if (!(error instanceof ApiProblem) || error.status !== 412) throw error;
      const remote = (
        await request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${ticketId}`)
      ).body;
      throw new FieldConflict(ticketId, ["deleted_at"], { deleted_at: true }, remote);
    }
  }

  for (const [ticketId, flat] of current) {
    const desired = new Set(flat.node.blockedBy || []);
    let existing = [...(cache.dependencies.get(ticketId) || [])];
    for (const edge of existing.filter((item) => !desired.has(item.blocking_ticket_id))) {
      const currentTicket = cache.tickets.get(ticketId);
      if (!currentTicket) continue;
      try {
        const removed = await request<void>(
          "DELETE",
          `/api/v1/boards/${boardId}/tickets/${ticketId}/dependencies/${edge.id}`,
          undefined,
          { etag: currentTicket.version },
        );
        const version = Number(String(removed.etag || "").replaceAll('"', ""));
        if (Number.isInteger(version)) currentTicket.version = version;
        existing = existing.filter((item) => item.id !== edge.id);
        cache.dependencies.set(ticketId, existing);
      } catch (error) {
        if (!(error instanceof ApiProblem) || error.status !== 412) throw error;
        const remote = await ticketConflictState(boardId, ticketId);
        throw new FieldConflict(ticketId, ["blockedBy"], { blockedBy: [...desired] }, remote);
      }
    }
    const present = new Set(existing.map((item) => item.blocking_ticket_id));
    for (const blockingId of desired) {
      if (present.has(blockingId)) continue;
      const currentTicket = cache.tickets.get(ticketId);
      if (!currentTicket) continue;
      try {
        const added = await request<Dependency>(
          "POST",
          `/api/v1/boards/${boardId}/tickets/${ticketId}/dependencies`,
          { blocking_ticket_id: blockingId },
          {
            etag: currentTicket.version,
            idempotencyKey: newIdempotencyKey(),
          },
        );
        const version = Number(String(added.etag || "").replaceAll('"', ""));
        if (Number.isInteger(version)) currentTicket.version = version;
        existing.push(added.body);
        present.add(blockingId);
        cache.dependencies.set(ticketId, existing);
      } catch (error) {
        if (!(error instanceof ApiProblem) || error.status !== 412) throw error;
        const remote = await ticketConflictState(boardId, ticketId);
        throw new FieldConflict(ticketId, ["blockedBy"], { blockedBy: [...desired] }, remote);
      }
    }
  }

  // Every command above returns the accepted resource and updates the cache.
  // Reloading the complete board here used to turn each autosave into a board,
  // columns, tickets, and per-ticket dependency fan-out. Return the accepted
  // local projection instead; a genuinely remote SSE event still reconciles
  // through loadBoard, while stale resource versions retain their deterministic
  // 412 retry path on the next mutation.
  const accepted: LegacyBoardDetail = {
    id: cache.board.id,
    name: document.name,
    role: cache.board.role,
    state: { cards: document.cards, view: document.view, tlUnit: document.tlUnit },
    version: cache.board.version,
    storage_mode: cache.board.storage_mode,
    columns: cache.columns.map((column) => ({
      id: column.key,
      name: column.name,
      terminal: column.terminal,
    })),
    id_mappings: idMappings,
  };
  cache.document = accepted;
  return accepted;
}

export async function saveBoard(
  boardId: string,
  document: LegacyDocument,
  baseline: LegacyDocument,
  version: number,
): Promise<LegacyBoardDetail> {
  const cache = cacheFor(boardId);
  if (!cache) {
    return (
      await request<LegacyBoardDetail>("PUT", `/api/boards/${boardId}`, {
        name: document.name,
        state: { cards: document.cards, view: document.view, tlUnit: document.tlUnit },
        base_version: version,
      })
    ).body;
  }
  return saveRelational(boardId, document, baseline);
}

export async function createBoard(name: string): Promise<LegacyBoardDetail> {
  const created = await request<Board>(
    "POST",
    "/api/v1/boards",
    { name },
    { idempotencyKey: newIdempotencyKey() },
  );
  return loadBoard(created.body.id);
}

export async function renameBoard(boardId: string, name: string): Promise<LegacyBoardDetail> {
  const cache = cacheFor(boardId);
  if (!cache) return loadBoard(boardId);
  await request<Board>("PATCH", `/api/v1/boards/${boardId}`, { name }, { etag: cache.boardEtag });
  return loadBoard(boardId);
}

export async function deleteBoard(boardId: string): Promise<void> {
  const cache = cacheFor(boardId);
  if (!cache) throw new Error("Load the board before deleting it");
  await request<void>("DELETE", `/api/v1/boards/${boardId}`, undefined, {
    etag: cache.boardEtag,
  });
  forgetBoard(boardId);
}

export async function listTrashedBoards(): Promise<Board[]> {
  const items: Board[] = [];
  let cursor: string | undefined;
  do {
    const params = new URLSearchParams({ limit: "200" });
    if (cursor) params.set("cursor", cursor);
    const page = (
      await request<{ items: Board[]; next_cursor?: string | null }>(
        "GET",
        `/api/v1/boards/trash?${params}`,
      )
    ).body;
    items.push(...page.items);
    cursor = page.next_cursor || undefined;
  } while (cursor);
  return items;
}

export async function restoreBoard(boardId: string, version: number): Promise<LegacyBoardDetail> {
  await request<Board>("POST", `/api/v1/boards/${boardId}/restore`, undefined, {
    etag: version,
    idempotencyKey: newIdempotencyKey(),
  });
  return loadBoard(boardId);
}

export async function purgeBoard(boardId: string, version: number): Promise<void> {
  await request<void>("DELETE", `/api/v1/boards/${boardId}/purge`, undefined, {
    etag: version,
  });
  forgetBoard(boardId);
}

export async function setBoardOrder(boardIds: string[]): Promise<void> {
  await request<void>(
    "PUT",
    "/api/v1/users/me/board-order",
    { board_ids: boardIds },
    { idempotencyKey: newIdempotencyKey() },
  );
}
